/**
 * Demolition live path: baked exterior atlas + tileable interior maps.
 * Procedural shaders remain the authoring source; this captures them once
 * per profile (and rebakes when look-dev settings change).
 */
import * as THREE from 'three';
import type { ProfileId } from '../../services/destructionProfiles';
import { getSurface } from '../MaterialRegistry';
import { DEFAULT_BRICK_SETTINGS } from '../authoring/BrickMaterial';
import { DEFAULT_CONCRETE_SETTINGS } from '../authoring/ConcreteMaterial';
import {
  assignTileableBoxUvs,
  BAKE_FRONT_SIZE,
  BAKE_SIDE_SIZE,
  bakeSurfaceMaps,
  bakeTileablePlanarMaps,
  INTERIOR_BAKE_SIZE,
  INTERIOR_TILE_METERS,
  packBakedFacesToAtlas,
  type BakeWallSpec,
  type BakedAtlasMaps,
  type BakedTileMaps,
} from './SurfaceBaker';
import { attachBakedDamageHook, type BakedDamageHookOpts } from './BakedSurfaceMaterial';
import { cloneBakedAtlasMaps, copyBakedAtlasMaps } from './DamageBakeDown';
import type { SurfaceFace } from './SurfaceDefinition';

export { assignTileableBoxUvs, INTERIOR_TILE_METERS, INTERIOR_BAKE_SIZE };

export const BAKED_PROFILE_IDS = ['concrete', 'brick', 'stone', 'wood', 'plaster'] as const;
export type BakedProfileId = (typeof BAKED_PROFILE_IDS)[number];

export interface DemolitionBakeHandle {
  list: THREE.MeshPhysicalMaterial[];
  outer: THREE.MeshPhysicalMaterial;
  inner: THREE.MeshPhysicalMaterial;
  atlas: BakedAtlasMaps;
  interior: BakedTileMaps;
  bakeMs: number;
  profileId: ProfileId;
  wall: BakeWallSpec;
  pristine: BakedAtlasMaps;
  lastBakeDownMs: number;
  restorePristine(renderer: THREE.WebGLRenderer): void;
  dispose(): void;
}

function coatFromSettings(settings: unknown): { clearcoat: number; clearcoatRoughness: number } {
  const s = settings as { clearcoat?: number; clearcoatRoughness?: number } | null;
  return {
    clearcoat: s?.clearcoat ?? 0,
    clearcoatRoughness: s?.clearcoatRoughness ?? 1,
  };
}

function hookOpts(
  profileId: ProfileId,
  face: SurfaceFace,
  settings: unknown,
): BakedDamageHookOpts {
  const s = (settings ?? {}) as {
    textureScale?: number;
    seed?: number;
    mortarWidth?: number;
    courseJitter?: number;
    blockIrregularity?: number;
  };
  const brick = profileId === 'brick';
  return {
    profileId,
    face,
    interiorAgeSettle: face === 'interior',
    textureScale: s.textureScale ?? DEFAULT_CONCRETE_SETTINGS.textureScale,
    seed: s.seed ?? DEFAULT_CONCRETE_SETTINGS.seed,
    brickTextureScale: brick ? (s.textureScale ?? DEFAULT_BRICK_SETTINGS.textureScale) : DEFAULT_BRICK_SETTINGS.textureScale,
    brickSeed: brick ? (s.seed ?? DEFAULT_BRICK_SETTINGS.seed) : DEFAULT_BRICK_SETTINGS.seed,
    brickMortarWidth: s.mortarWidth ?? DEFAULT_BRICK_SETTINGS.mortarWidth,
    brickCourseJitter: s.courseJitter ?? DEFAULT_BRICK_SETTINGS.courseJitter,
    stoneBlockIrregularity: s.blockIrregularity ?? 0.55,
  };
}

function makePhysical(
  maps: { albedo: THREE.Texture; roughness: THREE.Texture; normal: THREE.Texture },
  coat: { clearcoat: number; clearcoatRoughness: number },
  vertexColors: boolean,
): THREE.MeshPhysicalMaterial {
  const mat = new THREE.MeshPhysicalMaterial({
    color: 0xffffff,
    map: maps.albedo,
    roughnessMap: maps.roughness,
    roughness: 1,
    metalness: 0,
    normalMap: maps.normal,
    normalScale: new THREE.Vector2(1, 1),
    envMapIntensity: 1,
    vertexColors,
    flatShading: false,
    clearcoat: coat.clearcoat,
    clearcoatRoughness: coat.clearcoatRoughness,
    dithering: false,
  });
  mat.toneMapped = true;
  return mat;
}

export function bakeDemolitionProfile(
  renderer: THREE.WebGLRenderer,
  profileId: ProfileId,
  settings: unknown,
  wall: BakeWallSpec,
): DemolitionBakeHandle {
  const definition = getSurface(profileId);
  if (!definition) {
    throw new Error(`bakeDemolitionProfile: no surface for ${profileId}`);
  }
  const t0 = performance.now();
  const faceMaps = bakeSurfaceMaps(
    renderer,
    definition,
    settings,
    'exterior',
    { front: BAKE_FRONT_SIZE, side: BAKE_SIDE_SIZE },
    wall,
    { generateMipmaps: true, anisotropy: 8, keepRenderTargets: true },
  );
  const atlas = packBakedFacesToAtlas(renderer, faceMaps);
  faceMaps.dispose();
  const pristine = cloneBakedAtlasMaps(renderer, atlas);
  const interior = bakeTileablePlanarMaps(
    renderer,
    definition,
    settings,
    'interior',
    INTERIOR_BAKE_SIZE,
    INTERIOR_TILE_METERS,
    { generateMipmaps: true, anisotropy: 8, keepRenderTargets: true },
  );
  const coat = coatFromSettings(settings);
  const outer = makePhysical(atlas, coat, false);
  const inner = makePhysical(interior, coat, true);
  attachBakedDamageHook(outer, hookOpts(profileId, 'exterior', settings));
  attachBakedDamageHook(inner, hookOpts(profileId, 'interior', settings));
  const bakeMs = performance.now() - t0;
  const handle: DemolitionBakeHandle = {
    list: [outer, inner],
    outer,
    inner,
    atlas,
    interior,
    bakeMs,
    profileId,
    wall,
    pristine,
    lastBakeDownMs: 0,
    restorePristine(gl: THREE.WebGLRenderer) {
      copyBakedAtlasMaps(gl, handle.pristine, handle.atlas);
    },
    dispose() {
      outer.dispose();
      inner.dispose();
      atlas.dispose();
      pristine.dispose();
      interior.dispose();
    },
  };
  return handle;
}

export function isBakedProfile(id: string): id is BakedProfileId {
  return id === 'concrete' || id === 'brick' || id === 'stone' || id === 'wood' || id === 'plaster';
}

export interface ProfileBakeHandle {
  profileId: BakedProfileId;
  material: THREE.MeshPhysicalMaterial;
  maps: BakedTileMaps;
  bakeMs: number;
  tileMeters: number;
  dispose(): void;
}

export interface ProfileBakeSet {
  materials: Record<BakedProfileId, THREE.MeshPhysicalMaterial>;
  handles: Record<BakedProfileId, ProfileBakeHandle>;
  bakeMs: Record<BakedProfileId, number>;
  tileMeters: number;
  totalBakeMs: number;
  dispose(): void;
}

/**
 * One tileable exterior bake for a profile, independent of node size.
 * Cottage (and any multi-box structure) shares this material across pieces.
 */
export function bakeSharedProfile(
  renderer: THREE.WebGLRenderer,
  profileId: ProfileId,
  settings?: unknown,
): ProfileBakeHandle {
  if (!isBakedProfile(profileId)) {
    throw new Error(`bakeSharedProfile: ${profileId} is not a baked profile`);
  }
  const definition = getSurface(profileId);
  if (!definition) {
    throw new Error(`bakeSharedProfile: no surface for ${profileId}`);
  }
  const resolved = settings ?? definition.defaultSettings;
  const t0 = performance.now();
  const maps = bakeTileablePlanarMaps(
    renderer,
    definition,
    resolved,
    'exterior',
    INTERIOR_BAKE_SIZE,
    INTERIOR_TILE_METERS,
    { generateMipmaps: true, anisotropy: 8, keepRenderTargets: true },
  );
  const coat = coatFromSettings(resolved);
  const material = makePhysical(maps, coat, false);
  attachBakedDamageHook(material, hookOpts(profileId, 'exterior', resolved));
  const bakeMs = performance.now() - t0;
  return {
    profileId,
    material,
    maps,
    bakeMs,
    tileMeters: maps.tileMeters,
    dispose() {
      material.dispose();
      maps.dispose();
    },
  };
}

/** One bake per baked profile. Callers share the materials across every node. */
export function bakeSharedProfiles(
  renderer: THREE.WebGLRenderer,
  settingsFor?: (id: BakedProfileId) => unknown,
): ProfileBakeSet {
  const t0 = performance.now();
  const handles = {} as Record<BakedProfileId, ProfileBakeHandle>;
  const materials = {} as Record<BakedProfileId, THREE.MeshPhysicalMaterial>;
  const bakeMs = {} as Record<BakedProfileId, number>;
  try {
    for (const id of BAKED_PROFILE_IDS) {
      const handle = bakeSharedProfile(renderer, id, settingsFor?.(id));
      handles[id] = handle;
      materials[id] = handle.material;
      bakeMs[id] = handle.bakeMs;
    }
  } catch (error) {
    for (const handle of Object.values(handles)) handle.dispose();
    throw error;
  }
  return {
    materials,
    handles,
    bakeMs,
    tileMeters: INTERIOR_TILE_METERS,
    totalBakeMs: performance.now() - t0,
    dispose() {
      for (const handle of Object.values(handles)) handle.dispose();
    },
  };
}
